// Forgot-password — pick a channel and submit. The backend always
// returns 204; we always show the same "if-it-exists, expect a
// message" confirmation. This is anti-enumeration by design.

import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { merchantAuth } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/m/forgot-password')({
  component: MerchantForgotPassword,
});

function MerchantForgotPassword() {
  const { t } = useTranslation();
  const [merchantCode, setMerchantCode] = useState('');
  const [username, setUsername] = useState('');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [submitted, setSubmitted] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      merchantAuth.forgotPassword({
        merchant_code: merchantCode,
        username,
        channel,
      }),
    onSettled: () => setSubmitted(true),
  });

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" className="drop-shadow-sm" />
          <div className="space-y-1">
            <CardTitle className="text-xl">
              {t('merchant.forgot_password.title', { defaultValue: 'Reset your password' })}
            </CardTitle>
            <CardDescription>
              {t('merchant.forgot_password.subtitle', {
                defaultValue: 'Pick a channel and we will send you a recovery message.',
              })}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-3 text-sm">
              <p>
                {t('merchant.forgot_password.sent', {
                  defaultValue:
                    'If an account matches, a recovery message has been sent. Check your inbox or messages.',
                })}
              </p>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/m/login">
                  {t('merchant.forgot_password.back_to_login', {
                    defaultValue: 'Back to sign in',
                  })}
                </Link>
              </Button>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                submit.mutate();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="merchant">{t('merchants.code')}</Label>
                <Input
                  id="merchant"
                  required
                  autoFocus
                  value={merchantCode}
                  onChange={(e) => setMerchantCode(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="username">{t('auth.username')}</Label>
                <Input
                  id="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  {t('merchant.forgot_password.channel', { defaultValue: 'Send via' })}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={channel === 'email' ? 'default' : 'outline'}
                    onClick={() => setChannel('email')}
                  >
                    {t('merchant.forgot_password.email', { defaultValue: 'Email' })}
                  </Button>
                  <Button
                    type="button"
                    variant={channel === 'sms' ? 'default' : 'outline'}
                    onClick={() => setChannel('sms')}
                  >
                    {t('merchant.forgot_password.sms', { defaultValue: 'SMS' })}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={submit.isPending}>
                {t('merchant.forgot_password.submit', { defaultValue: 'Send recovery message' })}
              </Button>
              <div className="text-center text-xs text-muted-foreground pt-1">
                <Link to="/m/login" className="underline hover:text-foreground">
                  {t('merchant.forgot_password.back_to_login', {
                    defaultValue: 'Back to sign in',
                  })}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
